import { FC, KeyboardEvent } from "react";
import { useParams } from "react-router-dom";

import CloseIcon from "@mui/icons-material/Close";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from "@mui/material";
import { Form, Formik } from "formik";

import { Translate } from "@/components/Application/Translate";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import CustomTextArea from "@/helpers/components/CustomFormFields/CustomTextarea";

import { ProcedureUpdateNoticeRequest } from "@/model/Competence/ProcedureUpdateNoticeRequest";

import { CreateNewProcedureUpdateNotice } from "@/service/ProcedureMutations";
import { RouteParams } from "@/service/Utilities";

interface NewProcedureUpdateNoticeProps {
	open: boolean;
	onClose: () => void;
}

export const NewProcedureUpdateNotice: FC<NewProcedureUpdateNoticeProps> = ({
	open,
	onClose,
}) => {
	const { id } = useParams<RouteParams>();
	const createNotice = CreateNewProcedureUpdateNotice();

	const MAX_LEN = 1000;

	const validate = (values: ProcedureUpdateNoticeRequest) => {
		const errors: Partial<Record<keyof ProcedureUpdateNoticeRequest, string>> =
			{};
		if (!values.Description?.trim()) errors.Description = "Required";
		if (values.Description && values.Description.trim().length < 5)
			errors.Description = "Please add a bit more detail.";
		return errors;
	};

	const onSubmit = async (values: ProcedureUpdateNoticeRequest) => {
		try {
			await createNotice(values);
			AlertRequest.success();
			onClose();
		} catch (error) {
			AlertRequest.failed();
			console.error(error);
		}
	};

	const fieldSx = {
		"& .MuiInputBase-root": { borderRadius: 2 },
		"& textarea": { resize: "vertical" as const },
	};
	const buttonSx = {
		height: 40,
		borderRadius: 2,
		textTransform: "none",
		fontWeight: 800,
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="sm"
			PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
			aria-labelledby="new-proc-update-title"
		>
			{/* Header */}
			<DialogTitle
				id="new-proc-update-title"
				sx={{ pr: 6, pb: 0.5, fontWeight: 800 }}
			>
				<Translate
					HTMLTag="span"
					className="heading"
					String="Procedure.New.Update.Notice"
				>
					New procedure update notice
				</Translate>
			</DialogTitle>

			{/* Close */}
			<IconButton
				aria-label="close"
				onClick={onClose}
				sx={{ position: "absolute", right: 8, top: 8 }}
			>
				<CloseIcon />
			</IconButton>

			<DialogContent dividers sx={{ pt: 1.5 }}>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					<Translate String="Procedure.New.Update.Notice.Subtitle">
						Tell users what changed and why. Keep it clear and concise.
					</Translate>
				</Typography>

				<Formik<ProcedureUpdateNoticeRequest>
					initialValues={{ ProcedureId: id, Description: "" }}
					validate={validate}
					onSubmit={onSubmit}
					validateOnMount
				>
					{({
						values,
						errors,
						touched,
						isValid,
						dirty,
						isSubmitting,
						setFieldValue,
						submitForm,
					}) => {
						const len = values.Description?.length ?? 0;

						const onTextKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
							const isCmdEnter = (e.metaKey || e.ctrlKey) && e.key === "Enter";
							if (isCmdEnter && isValid && dirty && !isSubmitting) {
								e.preventDefault();
								submitForm();
							}
						};

						return (
							<Form>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
												mb: 0.5,
											}}
										>
											<EditNoteOutlinedIcon fontSize="small" />
											<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
												<Translate String="Procedure.Change.Description">
													Change description
												</Translate>
											</Typography>
											<Box sx={{ ml: "auto" }}>
												<Typography variant="caption" color="text.secondary">
													{len}/{MAX_LEN}
												</Typography>
											</Box>
										</Box>

										<CustomTextArea
											name="Description"
											id="Description"
											type="text"
											fullWidth
											multiline
											minRows={4}
											maxRows={18}
											placeholder="e.g. Updated inspection frequency from monthly to bi-weekly due to vendor advisory."
											sx={fieldSx}
											inputProps={{ maxLength: MAX_LEN }}
											onKeyDown={onTextKeyDown}
											// In case your CustomTextArea doesn’t pass inputProps through, you can also trim in onChange:
											onChange={(e: any) => {
												const v = String(e.target.value).slice(0, MAX_LEN);
												setFieldValue("Description", v);
											}}
											error={Boolean(touched.Description && errors.Description)}
											helperText={
												touched.Description && errors.Description
													? errors.Description
													: " "
											}
										/>
									</Grid>
								</Grid>

								{/* Footer */}
								<DialogActions sx={{ pt: 2, mt: 0.5, px: 0, gap: 1 }}>
									<Button
										variant="outlined"
										color="warning"
										startIcon={<CloseIcon />}
										onClick={onClose}
										sx={buttonSx}
									>
										<Translate String="Procedure.Button.Close">Close</Translate>
									</Button>
									<Button
										variant="contained"
										color="success"
										startIcon={<SaveIcon />}
										type="submit"
										disabled={!isValid || !dirty || isSubmitting}
										sx={buttonSx}
									>
										<Translate String="Procedure.Node.Create.Save">
											Save
										</Translate>
									</Button>
								</DialogActions>
							</Form>
						);
					}}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};
